import { useCallback, useEffect, useState } from "react";
import { api, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ADMIN, AI } from "@/constants/testIds";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, Save, Sparkles } from "lucide-react";
import ThankYouDialog from "@/components/ThankYouDialog";
import UpdateWriter from "@/components/UpdateWriter";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("rally_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [data, setData] = useState(null);
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [thankYou, setThankYou] = useState({ open: false, record: null, kind: "donation" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/overview", { headers: authHeaders() });
      setData(data);
      setGoal(String(data.stats.goal));
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("rally_admin_token");
        setToken("");
      }
    }
  }, []);

  useEffect(() => { if (token) load(); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem("rally_admin_token", data.token);
      setToken(data.token);
      toast.success("Logged in");
    } catch (err) {
      toast.error("Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("rally_admin_token");
    setToken("");
    setData(null);
  };

  const saveGoal = async () => {
    try {
      await api.put("/admin/goal", { goal: Number(goal) }, { headers: authHeaders() });
      toast.success("Goal updated");
      load();
    } catch (e) {
      toast.error("Could not save");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-blueprint flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-slate-950 p-8">
          <div className="font-mono text-[10px] uppercase tracking-widest text-kinetic">// Fleet command</div>
          <h1 className="font-display text-2xl font-black uppercase tracking-tight mt-2">Admin login</h1>
          <div className="mt-6 space-y-4">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Email</Label>
              <Input data-testid={ADMIN.email} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none bg-slate-900 border-white/15" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Password</Label>
              <Input data-testid={ADMIN.password} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none bg-slate-900 border-white/15" />
            </div>
            <Button data-testid={ADMIN.loginBtn} disabled={busy} className="w-full rounded-none bg-kinetic hover:bg-[#FF5E1A] text-black font-bold uppercase">
              {busy ? "Signing in…" : "Sign In"}
            </Button>
            <Link to="/" className="block text-center font-mono text-xs text-slate-500 hover:text-slate-300">← Back to site</Link>
          </div>
        </form>
      </div>
    );
  }

  const s = data?.stats;
  return (
    <div className="min-h-screen bg-blueprint">
      <header className="border-b border-white/10 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-kinetic">// Fleet command</div>
            <div className="font-display font-black text-xl uppercase tracking-tight">Admin Dashboard</div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white">View Site</Link>
            <Button data-testid={ADMIN.logoutBtn} onClick={logout} variant="outline" className="rounded-none border-white/15 bg-transparent">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8">
        {s && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat label="Raised" value={`$${s.raised.toLocaleString()}`} accent />
            <Stat label="Donated" value={`$${s.donated.toLocaleString()}`} />
            <Stat label="Pledged" value={`$${s.pledged.toLocaleString()}`} />
            <Stat label="Backers" value={s.backers} />
            <Stat label="% of Goal" value={`${s.percent.toFixed(1)}%`} />
          </div>
        )}

        <div className="border border-white/10 bg-slate-950 p-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Campaign goal</div>
          <div className="flex items-end gap-3 mt-2 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Target (USD)</Label>
              <Input data-testid={ADMIN.goalInput} type="number" value={goal} onChange={(e) => setGoal(e.target.value)} className="rounded-none bg-slate-900 border-white/15 h-11 font-mono text-lg" />
            </div>
            <Button data-testid={ADMIN.goalSave} onClick={saveGoal} className="h-11 rounded-none bg-kinetic hover:bg-[#FF5E1A] text-black font-bold uppercase">
              <Save className="w-4 h-4 mr-2" /> Save Goal
            </Button>
          </div>
        </div>

        <Tabs defaultValue="donations">
          <TabsList className="rounded-none bg-slate-950 border border-white/10 p-0 h-auto">
            <TabsTrigger data-testid={ADMIN.tabDonations} value="donations" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-kinetic data-[state=active]:text-black px-4 py-2">Donations ({data?.donations?.length ?? 0})</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabPledges} value="pledges" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-kinetic data-[state=active]:text-black px-4 py-2">Pledges ({data?.pledges?.length ?? 0})</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabSubs} value="subs" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-kinetic data-[state=active]:text-black px-4 py-2">Subscribers ({data?.subscribers?.length ?? 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="donations" className="mt-4">
            <TableCard rows={data?.donations || []} columns={[
              { k: "created_at", h: "Date", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
              { k: "name", h: "Name" },
              { k: "email", h: "Email" },
              { k: "amount", h: "Amount", fmt: (v) => `$${Number(v || 0).toLocaleString()}` },
              { k: "tier_id", h: "Tier", fmt: (v) => v?.replace(/_/g, " ") || "—" },
              { k: "payment_status", h: "Status" },
              { k: "message", h: "Message", fmt: (v) => v || "—" },
              { k: "__thankyou", h: "", render: (r) => (
                <Button
                  data-testid={AI.thankYouBtn(r.id)}
                  onClick={() => setThankYou({ open: true, record: r, kind: "donation" })}
                  variant="outline"
                  className="rounded-none h-8 px-2 border-kinetic/40 bg-transparent hover:bg-kinetic hover:text-black text-kinetic font-mono text-[10px] uppercase"
                >
                  <Sparkles className="w-3 h-3 mr-1" /> Thank
                </Button>
              )},
            ]} />
          </TabsContent>

          <TabsContent value="pledges" className="mt-4">
            <TableCard rows={data?.pledges || []} columns={[
              { k: "created_at", h: "Date", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
              { k: "name", h: "Name" },
              { k: "email", h: "Email" },
              { k: "amount", h: "Amount", fmt: (v) => `$${Number(v || 0).toLocaleString()}` },
              { k: "tier_id", h: "Tier", fmt: (v) => v?.replace(/_/g, " ") || "—" },
              { k: "message", h: "Message", fmt: (v) => v || "—" },
              { k: "__thankyou", h: "", render: (r) => (
                <Button
                  data-testid={AI.thankYouBtn(r.id)}
                  onClick={() => setThankYou({ open: true, record: r, kind: "pledge" })}
                  variant="outline"
                  className="rounded-none h-8 px-2 border-kinetic/40 bg-transparent hover:bg-kinetic hover:text-black text-kinetic font-mono text-[10px] uppercase"
                >
                  <Sparkles className="w-3 h-3 mr-1" /> Thank
                </Button>
              )},
            ]} />
          </TabsContent>

          <TabsContent value="subs" className="mt-4">
            <TableCard rows={data?.subscribers || []} columns={[
              { k: "created_at", h: "Date", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
              { k: "email", h: "Email" },
            ]} />
          </TabsContent>
        </Tabs>

        <UpdateWriter />
      </main>

      <ThankYouDialog
        open={thankYou.open}
        onClose={() => setThankYou({ open: false, record: null, kind: "donation" })}
        record={thankYou.record}
        kind={thankYou.kind}
      />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-4 border ${accent ? "border-kinetic/60 bg-[rgba(255,69,0,0.05)]" : "border-white/10 bg-slate-950"}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${accent ? "text-kinetic" : "text-white"}`}>{value}</div>
    </div>
  );
}

function TableCard({ rows, columns }) {
  if (!rows.length) {
    return <div className="border border-dashed border-white/15 bg-slate-950/50 p-10 text-center text-slate-400 font-mono text-xs uppercase tracking-widest">No entries yet</div>;
  }
  return (
    <div className="border border-white/10 bg-slate-950 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-900/80 border-b border-white/10">
          <tr>
            {columns.map((c) => (
              <th key={c.k} className="text-left px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">{c.h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-white/5 hover:bg-white/[0.02]">
              {columns.map((c) => (
                <td key={c.k} className="px-4 py-3 text-slate-300 align-top">
                  {c.render ? c.render(r) : (c.fmt ? c.fmt(r[c.k]) : (r[c.k] ?? "—"))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
